import { RegularButton } from "@components/buttons"
import { NextImage } from "@components/misc"
import { Heading } from "@components/typography"
import { container, FlexBox } from "@components/wrapper"
import { HARISENIN_PUBLIC_PAGE_ASSETS } from "@constants/pictures"
import { SchoolDetail } from "@interfaces/school"
import clsx from "clsx"
import Image from "next/image"
import { FC } from "react"
import { BsDot } from "react-icons/bs"
import { IoMdCheckmark } from "react-icons/io"

export interface BootcampBannerProps {
  schoolDetail: SchoolDetail
  scroll: () => void
}

const AlumnusPicture = ({ url, position }: { url: string; position: string }) => {
  return (
    <div className={clsx("rounded-full border-2 border-new-black wh-10 absolute", position)}>
      <Image src={url} width={40} height={40} alt="alumnus" className="rounded-full wh-full" />
    </div>
  )
}

export const BootcampBanner: FC<BootcampBannerProps> = ({ schoolDetail, scroll }) => {
  if (!schoolDetail || !schoolDetail.school_banner) {
    return <></>
  }

  const userPictures = schoolDetail.user_picture

  return (
    <div className={clsx("relative")}>
      {/* Background Image */}
      <NextImage
        size={[1440, 789]}
        src={`${HARISENIN_PUBLIC_PAGE_ASSETS}/homepage_banner_3.webp`}
        alt="program banner"
        className={clsx("sm:w-full sm:h-[60vw] sm:object-cover", "sm-only:h-full sm-only:absolute")}
        priority
      />

      <div
        className={clsx(
          "absolute top-0 sm:left-0 sm:h-full sm:w-full sm:py-20 bg-[rgba(0,0,0,0.9)]",
          "sm-only:relative py-8"
        )}
      >
        <div
          className={clsx(
            container,
            "sm:flex sm:h-full sm:justify-between sm:my-auto sm:mx-auto items-center sm:gap-9",
            "grid sm-only:!w-full sm-only:relative gap-6"
          )}
        >
          {/* Left section of banner */}
          <div className={clsx("sm:w-1/2 h-fit", "grid sm:gap-9 gap-6")}>
            {/* Review */}
            <FlexBox className="text-white sm:gap-5" align="center">
              <FlexBox className="relative w-20 h-10 sm-only:hidden">
                <AlumnusPicture position="left-0" url={userPictures[0]} />
                <AlumnusPicture position="left-5" url={userPictures[1]} />
                <AlumnusPicture position="left-10" url={userPictures[2]} />
              </FlexBox>
              <FlexBox>
                <span className="text-sm font-bold flex items-center">
                  ⭐ {schoolDetail.average_rating}/5 <BsDot />
                </span>
                <span>Dari {schoolDetail.total_alumni} Total Alumni</span>
              </FlexBox>
            </FlexBox>

            <div className="grid gap-4">
              {/* Title */}
              <Heading
                headingLevel="h1"
                className={clsx(
                  "font-urbanist text-white sm:font-semibold sm:text-4xl sm:mb-4",
                  "text-2xl  mb-2"
                )}
                id="header1"
              >
                {schoolDetail.school_banner.banner_title}
              </Heading>
              {schoolDetail.school_banner.banner_description.slice(0, 3).map((list, idx) => (
                <FlexBox key={idx} className="gap-2.5">
                  <FlexBox
                    className={clsx("rounded-full bg-soft-blue sm:wh-6 wh-4.5", "sm-only:mt-[1px]")}
                    align="center"
                    justify="center"
                  >
                    <IoMdCheckmark className="text-blue sm-only:text-sm" />
                  </FlexBox>
                  <div className="text-white sm:text-xl max-w-[90%] text-sm">{list}</div>
                </FlexBox>
              ))}
            </div>

            {/* Button for scrolling*/}
            {Boolean(schoolDetail.school_program_type.length) && (
              <RegularButton
                onClick={scroll}
                id="btn-regist-class-banner"
                customPadding="sm:px-10 sm:py-3 py-2 px-4"
                className="sm:w-fit w-full"
                variant="solid-blue"
                textClassName="sm-only:text-sm"
              >
                Daftar Sekarang
              </RegularButton>
            )}
          </div>

          {/* Right section of banner */}
          <div className={clsx("sm:w-2/5 sm:h-fit")}>
            <NextImage
              size={[1440, 789]}
              src={schoolDetail.school_banner.banner_image}
              alt="program banner"
              className={clsx("sm:h-auto sm:max-w-full rounded-lg", "sm-only:h-full w-full")}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
